/**
 * The single HTTP function behind every generated endpoint (web and mobile).
 * Each app configures it once: base URL, where tokens live, current agency, current language.
 * On a 401 it refreshes the session once and retries, so users are not logged out every 15 min.
 */

export interface ApiConfig {
  baseUrl: string;
  /** "web": refresh token in an httpOnly cookie. "mobile": refresh token in secure storage. */
  client: "web" | "mobile";
  getAccessToken: () => string | null;
  setAccessToken: (token: string | null) => void;
  /** Mobile only. */
  getRefreshToken?: () => Promise<string | null> | string | null;
  setRefreshToken?: (token: string | null) => Promise<void> | void;
  getOrganization: () => string | null;
  getLanguage: () => string;
  onSessionExpired?: () => void;
}

let config: ApiConfig = {
  baseUrl: "",
  client: "web",
  getAccessToken: () => null,
  setAccessToken: () => undefined,
  getOrganization: () => null,
  getLanguage: () => "fr",
};

export function configureApi(next: Partial<ApiConfig>): void {
  config = { ...config, ...next };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API error ${status}`);
    this.name = "ApiError";
  }

  get detail(): string | undefined {
    const body = this.body as { detail?: unknown } | undefined;
    return typeof body?.detail === "string" ? body.detail : undefined;
  }
}

function headersFor(init: RequestInit): Headers {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  headers.set("Accept-Language", config.getLanguage());
  headers.set("X-Client", config.client);
  const token = config.getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const organization = config.getOrganization();
  if (organization) headers.set("X-Organization", organization);
  return headers;
}

let refreshing: Promise<boolean> | null = null;

/** One refresh at a time, shared by concurrent requests. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const refresh = config.client === "mobile" ? await config.getRefreshToken?.() : null;
      if (config.client === "mobile" && !refresh) return false;
      const response = await fetch(`${config.baseUrl}/api/v1/auth/token/refresh/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Client": config.client },
        body: JSON.stringify(refresh ? { refresh } : {}),
      });
      if (!response.ok) return false;
      const body = (await response.json()) as { access: string; refresh?: string };
      config.setAccessToken(body.access);
      if (body.refresh) await config.setRefreshToken?.(body.refresh);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function apiFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const send = () =>
    fetch(`${config.baseUrl}${url}`, { ...init, credentials: "include", headers: headersFor(init) });

  let response = await send();
  if (response.status === 401 && !url.includes("/auth/")) {
    if (await refreshSession()) response = await send();
    else config.onSessionExpired?.();
  }
  const text = await response.text();
  // D-142: a calendar invitation (text/calendar) comes back as text; everything else is JSON
  const headers = response.headers as Headers | undefined; // test doubles may omit them
  const type = (typeof headers?.get === "function" ? headers.get("Content-Type") : null) ?? "";
  const body: unknown = !text ? undefined : type.startsWith("text/calendar") ? text : JSON.parse(text);
  if (!response.ok) throw new ApiError(response.status, body);
  // Orval's fetch client expects { data, status, headers }.
  return { data: body, status: response.status, headers: response.headers } as T;
}
